// Link-code logic — the share / claim primitive behind code-based child
// linking, as locked by docs/revamp/05_PORTAL_DESIGN.md §1–§2.
//
// The owner SHARES a code; another adult ENTERS it to link as a viewer. Codes
// are human-friendly (read aloud / typed by hand): a fixed length drawn from an
// unambiguous charset (no 0/O, 1/I/L, U), shown grouped for legibility.
//
// MULTIPLE adults may link to the same child — claiming a code never fails on
// "already linked"; the claim simply awaits the owner's consent and, once
// granted, adds another viewer. Nothing here looks at who a person "is".
//
// Pure TS, no dependencies. Randomness is injectable so the generator stays
// testable and deterministic under test.

/**
 * Unambiguous code alphabet. Excludes the easily-confused 0/O, 1/I/L and the
 * vowel U (to avoid accidental words). 30 symbols, all upper-case.
 */
export const LINK_CODE_CHARSET = "ABCDEFGHJKMNPQRSTVWXYZ23456789";

/** Number of significant characters in a code (separators not counted). */
export const LINK_CODE_LENGTH = 8;

/** Display grouping: the canonical code is shown as two hyphen-joined blocks. */
export const LINK_CODE_GROUP = 4;

const VALID_CODE_RE = new RegExp(
  `^[${LINK_CODE_CHARSET}]{${LINK_CODE_LENGTH}}$`,
);

// The DEPLOYED generate_link_code RPC mints 10-char uppercase-hex codes
// (substr of a uuid). Real users hold ONLY this format today, so the claim
// validator must accept it (P2 — previously every minted code was rejected;
// the mobile app ships the same dual-format acceptance).
const MINTED_HEX_RE = /^[0-9A-F]{10}$/;

/**
 * Generate a fresh, human-friendly link code in canonical display form
 * (e.g. "ABCD-EF23"). `random` defaults to `Math.random`; inject a deterministic
 * source in tests. The output normalizes back to a valid code.
 */
export function generateLinkCode(random: () => number = Math.random): string {
  let raw = "";
  for (let i = 0; i < LINK_CODE_LENGTH; i += 1) {
    const index = Math.floor(random() * LINK_CODE_CHARSET.length);
    // Guard the boundary case random() === 1 (or a hostile injected source).
    const safe = Math.min(Math.max(index, 0), LINK_CODE_CHARSET.length - 1);
    raw += LINK_CODE_CHARSET[safe];
  }
  return formatLinkCode(raw);
}

/**
 * Normalise user-entered input to the canonical comparison form: trim, upper-case
 * and strip separators (spaces, hyphens, underscores). Does NOT validate — pass
 * the result to {@link isValidLinkCode}.
 */
export function normalizeLinkCode(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[\s\-_]/g, "");
}

/**
 * True when `code` (in any entered casing / spacing) normalises to exactly
 * {@link LINK_CODE_LENGTH} characters drawn from {@link LINK_CODE_CHARSET}.
 */
export function isValidLinkCode(code: string): boolean {
  const normalized = normalizeLinkCode(code);
  return VALID_CODE_RE.test(normalized) || MINTED_HEX_RE.test(normalized);
}

/**
 * Render a normalised code in grouped display form (e.g. "ABCD-EF23"). Input is
 * normalised first, so this is safe to call on raw or already-grouped values.
 */
export function formatLinkCode(code: string): string {
  const normalized = normalizeLinkCode(code);
  const groups: string[] = [];
  for (let i = 0; i < normalized.length; i += LINK_CODE_GROUP) {
    groups.push(normalized.slice(i, i + LINK_CODE_GROUP));
  }
  return groups.join("-");
}

// --- Claim state machine -----------------------------------------------------
//
// An adult who enters a code travels: entered → pendingOwnerConsent → either
// linkedAsViewer or rejected. The two outcomes are terminal. The owner is the
// only party who can approve or reject — consent is never implied.

/**
 * Lifecycle of one claim attempt.
 *  - `entered`              an adult has supplied a (valid) code.
 *  - `pendingOwnerConsent`  the claim is awaiting the owner's decision.
 *  - `linkedAsViewer`       owner approved → a viewer link exists (terminal).
 *  - `rejected`             owner declined (terminal).
 */
export type ClaimState =
  | "entered"
  | "pendingOwnerConsent"
  | "linkedAsViewer"
  | "rejected";

/** The action driving a claim forward. */
export type ClaimEvent = "submit" | "ownerApprove" | "ownerReject";

/** The starting state of any claim. */
export const INITIAL_CLAIM_STATE: ClaimState = "entered";

const CLAIM_TRANSITIONS: Record<ClaimState, Partial<Record<ClaimEvent, ClaimState>>> = {
  entered: { submit: "pendingOwnerConsent" },
  pendingOwnerConsent: {
    ownerApprove: "linkedAsViewer",
    ownerReject: "rejected",
  },
  // Terminal — no further transitions. (Re-claiming starts a fresh `entered`.)
  linkedAsViewer: {},
  rejected: {},
};

/** True when no further claim transition is possible from `state`. */
export function isTerminalClaimState(state: ClaimState): boolean {
  return state === "linkedAsViewer" || state === "rejected";
}

/** The next state for `event` from `state`, or `null` if it isn't allowed. */
export function nextClaimState(
  state: ClaimState,
  event: ClaimEvent,
): ClaimState | null {
  return CLAIM_TRANSITIONS[state][event] ?? null;
}

/** True when `event` is a legal transition from `state`. */
export function canClaimTransition(state: ClaimState, event: ClaimEvent): boolean {
  return nextClaimState(state, event) !== null;
}

/**
 * Advance a claim. Throws on an illegal transition (e.g. approving an already
 * terminal claim) so callers can't silently drop an event.
 */
export function claimTransition(state: ClaimState, event: ClaimEvent): ClaimState {
  const next = nextClaimState(state, event);
  if (next === null) {
    throw new Error(`Illegal claim transition: ${event} from ${state}`);
  }
  return next;
}
